import store from '@/vuex/store';
import { v1 as uuid } from 'uuid';
import sha256 from 'js-sha256';
import { getCampaignEventData } from '@/modules/utm-persistence';

//  ================================
//  = Google Tag Manager =
//  ================================
//

// Tracks when unfocusing on a form fields on Login, Register, Forgotten Password, Reset Password & Brochure Request
export const formProgression = (formData) => {
  const targetElement = formData.target;

  // Check if the target element is an HTML element and has a closest method
  if (targetElement instanceof HTMLElement && typeof targetElement.closest === 'function') {
    const formElement = targetElement.closest('form');

    // If the form element exists and has a name, push form progression data to dataLayer
    if (formElement && formElement.name) {
      const formName = formElement.name;
      dataLayer.push({
        event: 'formProgression',
        formName,
        fieldLabel: targetElement.name
      });
    }
  }
};

// Tracks general events with ecommerce data
export const event = (event, ecommerce) => {
  dataLayer.push({
    event: event,
    ecommerce: ecommerce
  });
};

// Formats a string by converting it to lowercase and removing all spaces.
export const formatZiCode = (str) => {
  return str.toLowerCase().replace(/\s+/g, '');
};

// Builds the brochure-by-post payload. Exposed separately so the submit flow can stage it
// across the thank-you redirect instead of pushing it into a document that is about to unload.
export const brochureRequestPostPayload = (id, customer, method = 'post') => {
  return {
    event: 'brochure_request_post',
    id: uuid(),
    _clear: true,
    event_data: {
      method
    },
    user: {
      city: customer.city.toLowerCase(),
      country: customer.country.toLowerCase(),
      email: customer.email.toLowerCase(),
      email_sha256: sha256(customer.email),
      first_name: customer.firstName.toLowerCase(),
      last_name: customer.lastName.toLowerCase(),
      phone: customer.phone,
      zipcode: formatZiCode(customer.postal)
    }
  };
};

// Tracks the brochure download / view-online journey. The event name is historical and is
// deliberately unchanged — the agency's GTM triggers key on this exact string. `method`
// distinguishes the journeys instead.
export const brochureRequestEmail = (id, customer, method = 'email') => {
  dataLayer.push({
    event: 'brochure_request_email',
    id: uuid(),
    _clear: true,
    event_data: {
      method
    },
    user: {
      email: customer.email.toLowerCase(),
      email_sha256: sha256(customer.email),
      first_name: customer.firstName.toLowerCase(),
      last_name: customer.lastName.toLowerCase()
    }
  });
};

// Tracks when tour sticky navigation item has been clicked
export const holidayAnchor = (holidayName, anchorType) => {
  dataLayer.push({
    event: 'holidayAnchor',
    holidayName,
    anchorType
  });
};

// Tracks when a holiday search has been submitted
export const searchEvent = (searchWhere, searchWhen, searchHow, searchDepart) => {
  dataLayer.push({
    event: 'searchEvent',
    searchFilter: `Destination: ${searchWhere}, Departing Date: ${searchWhen}, Travel Type: ${searchHow}, Departure: ${searchDepart}`
  });
};

// Formats dates from 'Aug 2024' to '2024-08'
const formatDates = (dates) => {
  return dates.map(date => {
    let [month, year] = date.split(' ');
    month = month.charAt(0).toUpperCase() + month.slice(1).toLowerCase();
    const formattedMonth = monthMapping[month];
    return `${year}-${formattedMonth}`;
  });
}

// Tracks when a holiday search has been submitted
export const searchEventNew = (searchWhere, searchWhen, searchHow, searchDepart) => {
  const sortedDeparture = Array.isArray(searchDepart) ? searchDepart.map(value => value.toLowerCase()).sort() : [];
  const departureMonths = formatDates(searchWhen);
  const holidayTypes = Array.isArray(searchHow) ? searchHow.map(type => type.toLowerCase()).sort() : [];

  const filters = {};

  if (sortedDeparture.length > 0) {
    filters.departure_airport = sortedDeparture;
  }
  if (departureMonths.length > 0) {
    filters.departure_month = departureMonths;
  }
  if (holidayTypes.length > 0) {
    filters.holiday_type = holidayTypes;
  }
  
  dataLayer.push({
    event: 'search',
    id: uuid(),
    _clear: true,
    event_data: {
      search_term: searchWhere
    },
    filters: filters
  });
};

// Helper function to get selected values from a filter
const getSelectedValues = (itemsInfo, filterKey) => {
  const filter = itemsInfo.filters.find(filter => filter.value === filterKey);

  if (filter && filter.children) {
    const selectedItems = filter.children.filter(item => item.selected);
    const selectedLabels = selectedItems.map(item => item.label.toLowerCase());

    return selectedLabels;
  } else {
    return [];
  }
}

// Helper function to get min and max values from a filter
const getFilterValue = (itemsInfo, filterKey) => {
  const filter = itemsInfo.filters.find(filter => filter.value === filterKey);
  let min = null;
  let max = null;
  filter.children.forEach(item => {
    if (item.selected) {
      if (min === null || item.from < min) {
        min = item.from;
      }
      if (max === null || item.to > max) {
        max = item.to;
      }
    }
  });
  return { min, max };
}

const monthMapping = {
  Jan: '01',
  Feb: '02',
  Mar: '03',
  Apr: '04',
  May: '05',
  Jun: '06',
  Jul: '07',
  Aug: '08',
  Sep: '09',
  Oct: '10',
  Nov: '11',
  Dec: '12'
};

// Adds a filter to the filters object if the value is valid
const addFilterIfValid = (filters, key, value) => {
  if (value !== null && value !== undefined && !(Array.isArray(value) && value.length === 0)) {
    filters[key] = value;
  }
};

// Tracks item list on search results page
export const viewItemList = (itemsInfo) => {
  const priceValues = getFilterValue(itemsInfo, 'Prices');
  const durationValues = getFilterValue(itemsInfo, 'Durations');

  let filters = {};
  
  addFilterIfValid(filters, 'continents', getSelectedValues(itemsInfo, 'Continents'));
  addFilterIfValid(filters, 'countries', getSelectedValues(itemsInfo, 'Countries'));
  addFilterIfValid(filters, 'departure_airport', getSelectedValues(itemsInfo, 'Departs'));
  addFilterIfValid(filters, 'departure_month', formatDates(getSelectedValues(itemsInfo, 'Dates')));
  addFilterIfValid(filters, 'duration_days_max', durationValues.max);
  addFilterIfValid(filters, 'duration_days_min', durationValues.min);
  addFilterIfValid(filters, 'holiday_type', getSelectedValues(itemsInfo, 'Types'));
  addFilterIfValid(filters, 'price_max', priceValues.max);
  addFilterIfValid(filters, 'price_min', priceValues.min);

  const itemsArray = itemsInfo.items.map((item, index) => {
    const itemObject = {
      item_brand: item.brand,
      item_category: 'tours',
      discount: item.discount,
      index: index + 1,
      item_id: item.tourCode,
      item_list_name: 'search results',
      item_name: item.name,
      price: item.tourSummary.fromPrice
    };

    if (item.continent) {
      itemObject.continent = item.continent;
      itemObject.item_category2 = item.continent;
    }

    if (item.country) {
      itemObject.country = item.country;
      itemObject.item_category3 = item.country;
    }

    return itemObject;
  });

  dataLayer.push({
    event: 'view_item_list',
    id: uuid(),
    _clear: true,
    event_data: {
      //currency: itemsInfo.items[0].tourSummary.currency.toLowerCase(),
      search_results_count: itemsInfo.itemsCount
    },
    filters: filters,
    items: itemsArray
  });
}

// Tracks when login form has been submitted
export const loginEvent = (userId, userEmail) => {
  const userData = {
    email: userEmail.toLowerCase(),
    email_sha256: sha256(userEmail)
  };

  if (userId) {
    userData.id = userId;
  }

  dataLayer.push({
    event: 'login',
    id: uuid(),
    _clear: true,
    user: userData
  });
};
 
// Tracks when registration form has been submitted
export const registrationEvent = (userId, userEmail) => {
  dataLayer.push({
    event: 'sign_up',
    id: uuid(),
    _clear: true,
    user: {
      email: userEmail.toLowerCase(),
      email_sha256: sha256(userEmail)
      // id: userId
    },
  });
};
 
// Tracks on form submit for: Forgotten Password & Reset Password
export const formSubmitEvent = (formName) => {
  dataLayer.push({
    event: 'formSubmit',
    formName: formName.toLowerCase()
  });
};

// Tracks newsletter subscribe. `channels` are the marketing channels the customer actually
// opted into (e.g. ['email', 'post']) — callers must only invoke this when at least one was
// ticked, so the event means a sign-up rather than merely a completed form.
export const newsletterSubscribePayload = (customer, isBrochure = false, channels = []) => {
  return {
    event: 'newsletter_subscribe',
    id: uuid(),
    _clear: true,
    event_data: {
      method: isBrochure ? 'brochure_request' : 'checkout',
      channels: channels.join(',')
    },
    user: {
      country: isBrochure 
        ? (customer.address && customer.address.country || '').toLowerCase()
        : (customer.address && customer.address.fields && customer.address.fields.country && customer.address.fields.country.value && customer.address.fields.country.value.name || '').toLowerCase(),
      email: (customer.fields && customer.fields.email && customer.fields.email.value || '').toLowerCase(),
      email_sha256: customer.fields && customer.fields.email && customer.fields.email.value ? sha256(customer.fields.email.value) : '',
      first_name: (customer.firstName && customer.firstName.value || '').toLowerCase(),
      last_name: (customer.lastName && customer.lastName.value || '').toLowerCase()
    },
  };
};

export const newsletterSubscribe = (customer, isBrochure = false, channels = []) => {
  dataLayer.push(newsletterSubscribePayload(customer, isBrochure, channels));
};

// Formats the date of birth (DOB) in the format 'YYYYMMDD'.
// It ensures that the month and day values are always two digits by padding them with leading zeros if necessary.
export const formatDob = (year, month, day) => {
  return `${year}${String(month).padStart(2, '0')}${String(day).padStart(2, '0')}`;
};

// Tracks on page view
export const pageViewEvent = (notParsedPageData, userData) => {
  const pageData = JSON.parse(notParsedPageData);

  const page = {
    category: pageData.category.toLowerCase(),
    continent: pageData.continent.toLowerCase(),
    country: pageData.country.toLowerCase(),
    name: pageData.name.toLowerCase(),
    psuedo_path: pageData.pseudoPath
  };

  Object.keys(page).forEach(key => {
    if (page[key] === '' || page[key] === undefined) {
      delete page[key];
    }
  });

  const user = userData ? {
    city: userData.address.town.toLowerCase(),
    country: typeof userData.address.country === 'string'
      ? userData.address.country.toLowerCase()
      : userData.address.country.name.toLowerCase(),
    dob: formatDob(userData.yearOfBirth, userData.monthOfBirth, userData.dayOfBirth),
    email: userData.email ? userData.email.toLowerCase() : '',
    email_sha256: userData.email ? sha256(userData.email).toString() : '',
    first_name: userData.firstName.toLowerCase(),
    id: userData.clientId,
    last_name: userData.lastName.toLowerCase(),
    phone: userData.mobilePhone,
    zipcode: formatZiCode(userData.address.postCode),
    total_orders: userData.totalOrders
  } : {};

  Object.keys(user).forEach(key => {
    if (user[key] === '' || user[key] === undefined || user[key] === null) {
      delete user[key];
    }
  });

  const dataLayerObject = {
    event: 'view_page',
    id: uuid(),
    _clear: true,
    page: page
  };

  if (Object.keys(user).length > 0) {
    dataLayerObject.user = user;
  }

  dataLayer.push(dataLayerObject);

  // deferred-third-party-init.ts waits for this before loading GTM early, so Page View tags
  // never fire ahead of the page/user data.
  window.__tsPageViewPushed = true;
  window.dispatchEvent(new Event('ts:page-view-pushed'));
};

// User encounters an error during checkout
export const checkoutError = (errorTitle) => {
  dataLayer.push({
    event: 'checkout_error',
    id: uuid(),
    _clear: true,
    error: {
      message: errorTitle
    }
  });
};

// Maps passenger details to a user object for dataLayer
const mapUser = (passenger, totalOrders = '') => {
  if (!passenger) return {};
  const address = passenger.address || {};
  const addressFields = address.fields || {};
  const fields = passenger.fields || {};
  const townValue = (addressFields.town && addressFields.town.value) || passenger.addressTown || '';
  const countryValue = (addressFields.country && addressFields.country.value) || passenger.addressCountry;
  const emailValue = (fields.email && fields.email.value) || passenger.email || '';
  const user = {
    city: townValue ? townValue.toLowerCase() : '',
    country: typeof countryValue === 'string'
      ? (countryValue || '').toLowerCase()
      : (countryValue && countryValue.name || '').toLowerCase(),
    dob: (fields.dateOfBirth && fields.dateOfBirth.value) || '',
    email: (emailValue || '').toLowerCase(),
    email_sha256: emailValue ? sha256(emailValue) : '',
    first_name: ((fields.firstName && fields.firstName.value) || passenger.firstName || '').toLowerCase(),
    id: (fields.passengerId && fields.passengerId.value) || passenger.gtmPassengerId || '',
    last_name: ((fields.lastName && fields.lastName.value) || passenger.lastName || '').toLowerCase(),
    phone: (fields.mobilePhone && fields.mobilePhone.value) ||
           (fields.homePhone && fields.homePhone.value) ||
           passenger.phone ||
           '',
    zipcode: formatZiCode(
      (addressFields.postCode && addressFields.postCode.value) || passenger.addressPostCode || ''
    )
  };

  // If totalOrders is provided, add it to the user object
  if (totalOrders !== '') {
    user.total_orders = totalOrders;
  }

  return user;
};

// Calculates the duration of nights between two date-times
const getNightsDuration = (departureDateTime, returnDateTime) => {
  const departure = new Date(departureDateTime);
  const returnD = new Date(returnDateTime);
  const diffTime = returnD - departure;
  const diffDays = diffTime / (1000 * 60 * 60 * 24); 
  return Math.floor(diffDays);
};

// Extracts and formats departure details from the departure object
const extractDepartureDetails = (departure) => {
  if (!departure || !departure.departureLegs || !departure.returnLegs) return {};
  const departureLeg = departure.departureLegs.length > 0 ? departure.departureLegs[0] : null;
  const returnLeg = departure.returnLegs.length > 0 ? departure.returnLegs[0] : null;

  let departureDateTime, returnDateTime;

  if (departureLeg && returnLeg) {
    departureDateTime = departureLeg.departureTime.dateTime;
    returnDateTime = returnLeg.departureTime.dateTime;
  } else {
    departureDateTime = departure.departureNoFlights.date;
    returnDateTime = departure.returnNoFlights.date;
  }

  const nightsDuration = getNightsDuration(departureDateTime, returnDateTime);
  const departureDateObj = new Date(departureDateTime);
  const departureYear = departureDateObj.getFullYear();
  const departureMonth = departureDateObj.getMonth() + 1;
  const departureDate = departureDateObj.toISOString().split('T')[0];

  const result = {
    departure_date: departureDate,
    departure_month: `${departureYear}-${departureMonth < 10 ? '0' + departureMonth : departureMonth}`,
    nights_duration: nightsDuration
  };

  if (departureLeg && returnLeg) {
    result.airline = departureLeg.flightCarrier.name.toLowerCase();
    result.departure_airport = departureLeg.departureAirport.name.toLowerCase();
    result.departure_id = departureLeg.departureId;
  }

  return result;
};

// Formats a price to return its numeric value.
export const formatPrice = (price) => {
  if (typeof price === 'string') {
    // Remove non-numeric characters and convert to number
    return parseInt(price.replace(/[^0-9]/g, ''), 10);
  }
  return price;
};

// Adds items to the items array with a specified category
const addItems = (items, category) => {
  if (!items || !items.length) return [];
  return items.map((item) => {
    let newItem = { item_category: category };

    // Set item_id based on the available property (tourCode or id)
    if (item.tourCode) {
      newItem.item_id = item.tourCode;
    } else if (item.id) {
      newItem.item_id = item.id;
    }

    // Set item_name if available
    if (item.name) newItem.item_name = item.name.trim();

    // Copy selected fields from item to newItem
    ['brand', 'continent', 'country', 'discount', 'price', 'quantity', 'variant', 'carrierCode', 'airline'].forEach(field => {
      if (item[field]) {
        newItem[field] = item[field];
      }
    });

    return newItem;
  });
};

// Maps item details to a transaction item object for dataLayer
const mapItemToTransactionItem = (item, brand) => {
  let mappedItem = {
    item_category: item.category
  };

  // Determine the brand based on category
  if (item.item_category === 'flights') {
    mappedItem.item_brand = brand.toLowerCase();
  } else {
    mappedItem.item_brand = (brand || item.brand || '').toLowerCase();
  }

  // Map basic item properties to the mappedItem object
  if (item.item_id) mappedItem.item_id = item.item_id;
  if (item.item_name) mappedItem.item_name = item.item_name.toLowerCase();
  if (item.continent) mappedItem.item_category2 = item.continent;
  if (item.country) mappedItem.item_category3 = item.country;

  // Use carrierCode for item_id if it exists
  if (item.carrierCode) mappedItem.item_id = item.carrierCode;
  // Use airline for item_name if it exists
  if (item.airline) mappedItem.item_name = item.airline.toLowerCase();

  // Set price to 0 for flights
  if (item.item_category === 'flights') {
    mappedItem.price = 0;
  }

  // Map other item properties to the mappedItem object
  Object.keys(item).forEach(key => {
    if (item[key] && key !== 'category' && key !== 'item_id' && key !== 'item_name' && key !== 'brand' && key !== 'carrierCode' && key !== 'airline') {
      if (key === 'variant') {
        mappedItem[key] = item[key].toLowerCase();
      } else if (key === 'price') {
        mappedItem[key] = formatPrice(item[key]);
      } else {
        mappedItem[key] = item[key];
      }
    }
  });

  return mappedItem;
};

// Get number of rooms
export const getRoomNumbers = () => {
  return store.state.checkout.rooms.length;
};

// Get payment type from store
export const getPaymentType = () => {
  return store.state.checkout.paymentType;
};
 
// Get transaction type from store
export const getTransactionType = () => {
  return store.state.checkout.transactionType;
};

// Track view item(tour page)
export const viewItem = (dataLayerItem) => {
  let itemsList = [];

  if (dataLayerItem) {
    itemsList = itemsList.concat(addItems([dataLayerItem], 'tours'));
  }

  const brand = dataLayerItem.brand;

  dataLayer.push({
    event: 'view_item',
    id: uuid(),
    _clear: true,
    event_data: {
      currency: dataLayerItem.currency,
      nights_duration: dataLayerItem.nights,
      tour_code: dataLayerItem.tourCode,
    },
    items: itemsList.map(item => mapItemToTransactionItem(item, brand)),
  });
}

// Checkout flow starting here

// Track add to cart
export const addToCart = (dataLayerItem) => {
  let itemsList = [];
  let departureDetails = {};

  if (dataLayerItem && dataLayerItem.departure) {
    itemsList = itemsList.concat(addItems([dataLayerItem.departure.destinationDetails], 'tours'));
  }

  const brand = dataLayerItem.brand.toLowerCase();

  if(dataLayerItem.departure.details) {
    departureDetails = extractDepartureDetails(dataLayerItem.departure.details);
  }

  const dataLayerObj = {
    event: 'add_to_cart',
    id: uuid(),
    _clear: true,
    event_data: {
      ...departureDetails,
      currency: dataLayerItem.currency.toLowerCase(),
      tour_code: dataLayerItem.tourCode,
      ...getCampaignEventData(),
    },
    items: itemsList.map(item => mapItemToTransactionItem(item, brand))
  };

  dataLayer.push(dataLayerObj);
}

// Track add tour extension
export const addTourExtension = (dataLayerItem) => {
  let itemsList = [];
  let departureDetails = {};

  if (dataLayerItem && dataLayerItem.addons) {
    itemsList = itemsList.concat(addItems(dataLayerItem.addons, 'addons'));
  }

  const brand = dataLayerItem.data.brand.toLowerCase();

  if(dataLayerItem.departureDetails) {
    departureDetails = extractDepartureDetails(dataLayerItem.departureDetails);
  }

  dataLayer.push({
    event: 'add_tour_extension',
    id: uuid(),
    _clear: true,
    event_data: {
      ...departureDetails,
      currency: dataLayerItem.quote.currency.toLowerCase(),
      tour_code: dataLayerItem.quote.tourCode,
    },
    items: itemsList.map(item => mapItemToTransactionItem(item, brand)),
  });
}

// Track add begin checkout
export const beginCheckout = (dataLayerItem) => {
  let itemsList = [];
  let departureDetails = {};

  if (dataLayerItem && dataLayerItem.tours) {
    itemsList = itemsList.concat(addItems(dataLayerItem.tours, 'tours'));
  }

  const brand = dataLayerItem.data.brand;

  if(dataLayerItem.departureDetails) {
    departureDetails = extractDepartureDetails(dataLayerItem.departureDetails);
  }

  dataLayer.push({
    event: 'begin_checkout',
    id: uuid(),
    _clear: true,
    event_data: {
      ...departureDetails,
      currency: dataLayerItem.quote.currency.toLowerCase(),
      tour_code: dataLayerItem.quote.tourCode,
      ...getCampaignEventData(),
    },
    items: itemsList.map(item => mapItemToTransactionItem(item, brand)),
  });
}

// Track add package
export const addPackage = (dataLayerItem) => {
  let itemsList = [];
  let departureDetails = {};

  if (dataLayerItem && dataLayerItem.rooms) {
    itemsList = itemsList.concat(addItems(dataLayerItem.rooms, 'rooms'));
  }

  const brand = dataLayerItem.data.brand;

  if(dataLayerItem.departureDetails) {
    departureDetails = extractDepartureDetails(dataLayerItem.departureDetails);
  }

  dataLayer.push({
    event: 'add_package',
    id: uuid(),
    _clear: true,
    event_data: {
      ...departureDetails,
      currency: dataLayerItem.quote.currency.toLowerCase(),
      tour_code: dataLayerItem.quote.tourCode,
    },
    items: itemsList.map(item => mapItemToTransactionItem(item, brand)),
  });
}

// Track add Excursions
export const addExcursions = (dataLayerItem) => {
  let itemsList = [];
  let departureDetails = {};

  if (dataLayerItem && dataLayerItem.excursions) {
    itemsList = itemsList.concat(addItems(dataLayerItem.excursions, 'excursions'));
  }

  const brand = dataLayerItem.data.brand;

  if(dataLayerItem.departureDetails) {
    departureDetails = extractDepartureDetails(dataLayerItem.departureDetails);
  }

  dataLayer.push({
    event: 'add_excursions',
    id: uuid(),
    _clear: true,
    event_data: {
      ...departureDetails,
      currency: dataLayerItem.quote.currency.toLowerCase(),
      tour_code: dataLayerItem.quote.tourCode,
    },
    items: itemsList.map(item => mapItemToTransactionItem(item, brand)),
  });
}

// Track add shipping info
export const addShippingInfo = (dataLayerItem) => {
  let itemsList = [];
  let departureDetails = {};

  if (dataLayerItem && dataLayerItem.data.tours) {
    itemsList = itemsList.concat(addItems(dataLayerItem.data.tours, 'tours'));
  }
  if (dataLayerItem && dataLayerItem.data.excursions) {
    itemsList = itemsList.concat(addItems(dataLayerItem.data.excursions, 'excursions'));
  }
  if (dataLayerItem && dataLayerItem.data.bookingFlights) {
    itemsList = itemsList.concat(addItems(dataLayerItem.data.bookingFlights, 'flights'));
  }
  if (dataLayerItem && dataLayerItem.data.donationsData) {
    const donations = Array.isArray(dataLayerItem.data.donationsData)
      ? dataLayerItem.data.donationsData
      : [dataLayerItem.data.donationsData];
    itemsList = itemsList.concat(addItems(donations, 'donations'));
  }
  if (dataLayerItem && dataLayerItem.data.addons) {
    itemsList = itemsList.concat(addItems(dataLayerItem.data.addons, 'addons'));
  }

  const passenger = dataLayerItem.data.passengers[0];
  const brand = dataLayerItem.brand;

  if(dataLayerItem.data.departureDetails) {
    departureDetails = extractDepartureDetails(dataLayerItem.data.departureDetails);
  }

  dataLayer.push({
    event: 'add_shipping_info',
    id: uuid(),
    _clear: true,
    event_data: {
      ...departureDetails,
      currency: dataLayerItem.quote.currency.toLowerCase(),
      rooms: getRoomNumbers(),
      tour_code: dataLayerItem.quote.tourCode,
      travellers: dataLayerItem.quote.passengers.length,
    },
    items: itemsList.map(item => mapItemToTransactionItem(item, brand)),
    transaction: {
      value: dataLayerItem.data.bookingData.amountOwing
    },
    user: mapUser(passenger)
  });
};

// Track add payment info
export const addPaymentInfo = (dataLayerItem) => {
  let itemsList = [];
  let departureDetails = {};

  if (dataLayerItem && dataLayerItem.data.tours) {
    itemsList = itemsList.concat(addItems(dataLayerItem.data.tours, 'tours'));
  }
  if (dataLayerItem && dataLayerItem.data.excursions) {
    itemsList = itemsList.concat(addItems(dataLayerItem.data.excursions, 'excursions'));
  }
  if (dataLayerItem && dataLayerItem.data.bookingFlights) {
    itemsList = itemsList.concat(addItems(dataLayerItem.data.bookingFlights, 'flights'));
  }
  if (dataLayerItem && dataLayerItem.data.donationsData) {
    const donations = Array.isArray(dataLayerItem.data.donationsData)
      ? dataLayerItem.data.donationsData
      : [dataLayerItem.data.donationsData];
    itemsList = itemsList.concat(addItems(donations, 'donations'));
  }
  if (dataLayerItem && dataLayerItem.data.addons) {
    itemsList = itemsList.concat(addItems(dataLayerItem.data.addons, 'addons'));
  }

  const passenger = dataLayerItem.data.passengers[0];
  const brand = dataLayerItem.brand;

  if(dataLayerItem.data.departureDetails) {
    departureDetails = extractDepartureDetails(dataLayerItem.data.departureDetails);
  }

  dataLayer.push({
    event: 'add_payment_info',
    id: uuid(),
    _clear: true,
    event_data: {
      ...departureDetails,
      currency: dataLayerItem.quote.currency.toLowerCase(),
      rooms: getRoomNumbers(),
      tour_code: dataLayerItem.quote.tourCode,
      travellers: dataLayerItem.quote.passengers.length,
    },
    items: itemsList.map(item => mapItemToTransactionItem(item, brand)),
    transaction: {
      transaction_type: getTransactionType(),
      value: dataLayerItem.data.bookingData.amountOwing
    },
    user: mapUser(passenger)
  });
};

// User attempts to make a purchase
export const bookingPurchaseSubmit = (checkoutData) => {
  let itemsList = [];
  let departureDetails = {};

  if (checkoutData && checkoutData.data.tours) {
    itemsList = itemsList.concat(addItems(checkoutData.data.tours, 'tours'));
  }
  if (checkoutData && checkoutData.data.excursions) {
    itemsList = itemsList.concat(addItems(checkoutData.data.excursions, 'excursions'));
  }
  if (checkoutData && checkoutData.data.bookingFlights) {
    itemsList = itemsList.concat(addItems(checkoutData.data.bookingFlights, 'flights'));
  }
  if (checkoutData && checkoutData.data.donationsData) {
    const donations = Array.isArray(checkoutData.data.donationsData)
      ? checkoutData.data.donationsData
      : [checkoutData.data.donationsData];
    itemsList = itemsList.concat(addItems(donations, 'donations'));
  }
  if (checkoutData && checkoutData.data.addons) {
    itemsList = itemsList.concat(addItems(checkoutData.data.addons, 'addons'));
  }

  const passenger = checkoutData.passengers[0];
  const brand = checkoutData.brand;

  if(checkoutData.data.departureDetails) {
    departureDetails = extractDepartureDetails(checkoutData.data.departureDetails);
  }

  // Push the completed transaction
  dataLayer.push({
    event: 'purchase_submit',
    id: uuid(),
    _clear: true,
    event_data: {
      ...departureDetails,
      currency: checkoutData.quote.currency.toLowerCase(),
      rooms: getRoomNumbers(),
      tour_code: checkoutData.quote.tourCode,
      travellers: checkoutData.passengers.length,
      ...getCampaignEventData(),
    },
    items: itemsList.map(item => mapItemToTransactionItem(item, brand)),
    transaction: {
      paid_today: checkoutData.totalDeposit,
      transaction_type: checkoutData.data.transactionType,
      value: checkoutData.data.bookingData.amountOwing
    },
    user: mapUser(passenger),
  });
};

// User completes a successful purchase 
export const bookingPurchase = (checkoutData) => {
  let itemsList = [];
  let departureDetails = {};
  let totalOrders = 0;

  if (checkoutData && checkoutData.tours) {
    itemsList = itemsList.concat(addItems(checkoutData.tours, 'tours'));
  }
  if (checkoutData && checkoutData.excursions) {
    itemsList = itemsList.concat(addItems(checkoutData.excursions, 'excursions'));
  }
  if (checkoutData && checkoutData.bookingFlights) {
    itemsList = itemsList.concat(addItems(checkoutData.bookingFlights, 'flights'));
  }
  if (checkoutData && checkoutData.donationsData) {
    const donations = Array.isArray(checkoutData.donationsData)
      ? checkoutData.donationsData
      : [checkoutData.donationsData];
    itemsList = itemsList.concat(addItems(donations, 'donations'));
  }
  if (checkoutData && checkoutData.addons) {
    itemsList = itemsList.concat(addItems(checkoutData.addons, 'addons'));
  }

  const passenger = checkoutData.passengers[0];
  const brand = checkoutData.data.brand;

  if (passenger.totalOrders !== undefined) {
    totalOrders = passenger.totalOrders;
  }

  if(checkoutData.departureDetails) {
    departureDetails = extractDepartureDetails(checkoutData.departureDetails);
  }

  const isReservation = checkoutData.bookingType === "reserved";

  const transaction = {
    id: checkoutData.bookingId,
    transaction_type: isReservation ? "Reservation" : checkoutData.transactionType,
    value: checkoutData.bookingData.amountOwing
  };

  if (!isReservation) {
    transaction.paid_today = checkoutData.totalDeposit;
  }

  // Only add payment_type if it exists and is not empty/null/undefined
  if (checkoutData.paymentType) {
    transaction.payment_type = checkoutData.paymentType;
  }

  // Push the completed transaction
  dataLayer.push({
    event: 'purchase',
    id: uuid(),
    _clear: true,
    event_data: {
      ...departureDetails,
      currency: checkoutData.quote.currency.toLowerCase(),
      rooms: checkoutData.rooms.length,
      tour_code: checkoutData.quote.tourCode,
      travellers: checkoutData.passengers.length,
      ...getCampaignEventData(),
    },
    items: itemsList.map(item => mapItemToTransactionItem(item, brand)),
    transaction,
    user: mapUser(passenger, totalOrders)
  });
};

// Track cancelation button for reserved bookings
export const cancelationBooking = (checkoutData) => {
  dataLayer.push({
    event: 'optionCancellation',
    id: uuid(),
    _clear: true,
    event_data: {
      bookingId: checkoutData.bookingId,
      tourCode: checkoutData.quote.tourCode,
      currency: checkoutData.quote.currency.toLowerCase(),
      value: checkoutData.bookingData.amountOwing
    }
  });
};

// Track payment error during checkout
export const checkoutPaymentError = (errorMessage, errorCode, errorReason) => {
  dataLayer.push({
    event: 'payment_checkout_error',
    id: uuid(),
    _clear: true,
    error: {
      message: errorMessage,
      code: errorCode,
      reason: errorReason
    }
  });
};

// Track when chat is opened
export const chatOpened = () => {
  dataLayer.push({
    event: 'live_chat',
    id: uuid(),
    _clear: true
  });
};

// Tracks a click on any tel: link. Payload matches the shape the footer previously
// pushed inline, so the existing GTM trigger keeps matching.
export const phoneClick = (phoneNumber) => {
  dataLayer.push({
    event: 'contact_click_phone',
    id: uuid(),
    _clear: true,
    event_data: {
      click_text: phoneNumber,
      method: 'phone'
    }
  });
};

export default {
  setupVuexSubscribe() {
    store.subscribe((mutation) => {
      switch (mutation.type) {
      case 'validateField':
        return formProgression(mutation.payload);
      }
    });
  }
};

const eventClick = (eventData) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(eventData);

  requestAnimationFrame(() => {
    isClickHandled = false;
  });
}

document.addEventListener('click', function (event) {
  let isClickHandled = false;

  const link = event.target.closest('.hero-header .image-link-overlay');

  if (link) {
    if (isClickHandled) return;

    isClickHandled = true;

    const slideName = link.getAttribute('data-slide-name')?.toLowerCase() || '';
    const slideLink = link.getAttribute('data-slide-link');
    const slidePosition = parseInt(link.getAttribute('data-slide-position')) + 1;
    const slideUuid = link.getAttribute('data-slide-uuid');

    const eventData = {
      event: 'carousel_hero_header',
      id: slideUuid,
      _clear: true,
      event_data: {
        brand: '@brand',
        page: '@pageName',
        link: slideLink,
        slide_position: slidePosition,
        ...(slideName && { name: slideName })
      },
    };
    eventClick(eventData);
  }
});

document.addEventListener('DOMContentLoaded', function () {
  let isClickHandled = false;

  document.addEventListener('click', function (event) {
    const clickable = event.target.closest('.hero-header .isClickable');

    if (!clickable || isClickHandled) return;

    isClickHandled = true;

    event.preventDefault();
    event.stopPropagation();

    const eventData = {
      event: 'banner_hero_header',
      id: '@heroHeaderUuid',
      _clear: true,
      event_data: {
        brand: '@brand',
        page: '@pageName',
        link: '@Model.Content.BannerLink.Url',
        ...('@bannerLinkName' && { name: '@bannerLinkName' }),
      },
    };

    eventClick(eventData);
  });
});